import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [courseId, setCourseId] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      if (attempts.current >= 8) { setStatus("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setCourseId(data.course_id);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [params]);

  return (
    <div className="min-h-screen bg-[#050505] grid-bg flex items-center justify-center px-6">
      <div className="bg-[#0C0D12] border border-white/10 rounded-xl p-12 text-center max-w-md w-full">
        {status === "checking" && (
          <><Loader2 className="w-12 h-12 text-[#00E5FF] animate-spin mx-auto mb-6" />
          <h1 className="font-heading font-bold text-2xl mb-2">Confirmando tu pago...</h1>
          <p className="font-body text-[#8A8D98]">Esto tomará solo unos segundos.</p></>
        )}
        {status === "paid" && (
          <><div className="w-16 h-16 rounded-full bg-[#00E6BA]/15 flex items-center justify-center mx-auto mb-6"><CheckCircle2 className="w-9 h-9 text-[#00E6BA]" /></div>
          <h1 className="font-heading font-bold text-2xl mb-2">¡Pago exitoso!</h1>
          <p className="font-body text-[#8A8D98] mb-8">Ya tienes acceso completo al curso.</p>
          <Button data-testid="success-go-dashboard" onClick={() => navigate("/dashboard")} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12">Ir a mis cursos</Button></>
        )}
        {(status === "error" || status === "timeout") && (
          <><div className="w-16 h-16 rounded-full bg-destructive/15 flex items-center justify-center mx-auto mb-6"><XCircle className="w-9 h-9 text-destructive" /></div>
          <h1 className="font-heading font-bold text-2xl mb-2">{status === "timeout" ? "Verificación pendiente" : "Hubo un problema"}</h1>
          <p className="font-body text-[#8A8D98] mb-8">{status === "timeout" ? "Si el pago se realizó, aparecerá en tu panel en breve." : "No pudimos confirmar el pago."}</p>
          <Button onClick={() => navigate("/dashboard")} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12">Ir al panel</Button></>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505] grid-bg flex items-center justify-center px-6">
      <div className="bg-[#0C0D12] border border-white/10 rounded-xl p-12 text-center max-w-md w-full">
        <div className="w-16 h-16 rounded-full bg-[#FFB020]/15 flex items-center justify-center mx-auto mb-6"><XCircle className="w-9 h-9 text-[#FFB020]" /></div>
        <h1 className="font-heading font-bold text-2xl mb-2">Pago cancelado</h1>
        <p className="font-body text-[#8A8D98] mb-8">No se realizó ningún cargo. Puedes intentarlo de nuevo cuando quieras.</p>
        <Button data-testid="cancel-go-catalog" onClick={() => navigate("/cursos")} className="w-full bg-[#00E5FF] text-black font-semibold hover:bg-white rounded-md h-12">Volver al catálogo</Button>
      </div>
    </div>
  );
}
